import { CourtDto, SportType } from '../../court/dto/court.dto';
import {
  NextorePaymentIntentDto,
  PaymentIntentDto,
} from '../../payment/dto/payment-intent.dto';
import { UserDto } from '../../user/dto/user.dto';
import {
  ReplacementRequestStatus,
  ReplacementRequestType,
} from '../../payment/dto/replacement-request.dto';
import { GameCancellationPolicy } from '../../payment/dto/game-cancellation.dto';
import { GameAdminCommentDto } from './game-admin-comment.dto';
import { GameJoiningInvitationDto } from './game-joining-invitation.dto';
import { GameJoiningRequestDto } from './game-joining-request.dto';
import { RefundStatus } from './refund.dto';

export type GameDto = {
  id: number;
  startTime: Date;
  type: GameType;
  duration: number;
  capacity: number;
  participants: GameParticipantDto[];
  requests: GameJoiningRequestDto[];
  invitations: GameJoiningInvitationDto[];
  paymentIntents: PaymentIntentDto[];
  matches: Match[];
  organiser: UserDto;
  status: GameStatus;
  court: CourtDto;
  rankedMode: GameRankedMode;
  refundDeadline: number;
  // Optional per-tenant cancellation modal copy, sourced live from the
  // organization. null = mobile uses the translated default. The body
  // supports a {{hours}} placeholder that's interpolated client-side from
  // refundDeadline.
  cancellationMessageWithinDeadline: string | null;
  cancellationMessageOutsideDeadline: string | null;
  // Per-tenant gate for the multi-player pay flow. Surfaced on the game DTO
  // so the mobile join modal can show/hide the PlayerSelectionStep without a
  // second org-level fetch. Mirrors Organization.payForOthersEnabled.
  payForOthersEnabled: boolean;
  minLevel: number;
  maxLevel: number;
  sport: SportType;
  creationTime: Date;
  fullyPaid: boolean;
  paidSlots?: number;
  completedAt?: Date;
  // Per-game opt-in for auto-triggered court recording when the game
  // transitions to COMPLETED on a court with an active VenueAgent.
  recordingEnabled: boolean;
  adminComments: GameAdminCommentDto[];
  /**
   * ⚠️ THE UNIT DEPENDS ON WHICH ENDPOINT FILLED THIS IN. Do not divide it, do
   * not multiply it, and do not add a new consumer without reading this.
   *
   * MAJOR units (euros) on both rails (D8), but a different QUANTITY on each:
   *
   * - `GET /games/:id/details` (`game.controller.ts`) sets it to the WHOLE
   *   COURT amount — `base - discount + applicationFees` for the entire game,
   *   i.e. roughly `capacity` times what one player pays.
   * - The Nextore search rail (`nextore.controller.ts`) sets it to what ONE
   *   joiner pays — the last row of `calculateGameJoiningFeesWithCart`.
   *
   * Both are load-bearing for a live consumer (the dashboard divides the first
   * by `capacity`; the POS bills the second as a seat price), so neither can be
   * changed without breaking one of them. **New code wants
   * `defaultJoiningPricePerPlayer` below, which means the same thing on every
   * rail.** This field is kept only for those two existing consumers.
   */
  defaultJoiningPrice?: number;
  /**
   * What ONE player is charged to join this game, in MAJOR units (D8).
   *
   * Unlike `defaultJoiningPrice` above, this means the same thing on every rail
   * that sets it, and it is not derived by dividing anything: it is the
   * `perPersonAmount` produced by the very calculator every joining charge rail
   * bills from (`calculateGameJoiningPriceWithApplicationFees` →
   * `computeBookingCostBreakdown`), so a client that renders it shows the
   * amount that will actually be taken (D5, D6).
   *
   * "Default" in the same sense as `defaultJoiningPrice`: it is priced with no
   * user, so it carries the organization's DEFAULT membership discount
   * (MYS-116), not the discount of whichever member ends up joining. It is also
   * NOT `defaultJoiningPrice / capacity` — a membership discount is one-seat
   * scoped, so the whole-court figure divided by capacity is larger than this
   * by up to `discount * (capacity - 1) / capacity`.
   *
   * ABSENT (never 0) when the game cannot be priced — a `0` on a card or a till
   * reads as "free". See the `catch` in `game.controller.ts`.
   */
  defaultJoiningPricePerPlayer?: number;
  orderCount?: number;
  replacementRequests: GameReplacementRequestDto[];
  refunds: GameRefundDto[];
  replacedFromGameId?: number;
  // True when an operator created this game via the dashboard with no payment
  // collection (a "comped" reservation). Mobile uses this to label the slot
  // "offert par {venue}" and skip any pay-share UI.
  isComped: boolean;
  // Audit trail: the operator user id that initiated this admin booking
  // (STRIPE_LINK or COMPED). Undefined for non-admin bookings. Surfaced to
  // dashboard for "who comped this?" attribution; not surfaced to mobile.
  bookedByOperatorId?: string;
  cancellationPolicy?: GameCancellationPolicy;
  // Admin view only: covered players who are no longer seated (kicked, or left)
  // and whose share nobody has refunded yet — the dashboard's payments table
  // renders them as "former participant" rows with a refund-seat button.
  // Absent for every non-admin viewer.
  formerCoveredSeats?: FormerCoveredSeatDto[];
};

export type NextoreGameDto = Omit<GameDto, 'paymentIntents' | 'fullyPaid'> & {
  paymentIntents: NextorePaymentIntentDto[];
};
export type GameParticipantDto = {
  participant: UserDto;
  position: number;
  createdAt: string;
  // Optional: when present and different from `participant`, indicates this
  // slot was paid for by another user (pay-for-others feature). Old clients
  // that don't render this just ignore it.
  paidBy?: UserDto;
  // Held seat (pay-for-others consent): true until the covered player accepts
  // the seat `paidBy` paid for. Absent/false on every other seat.
  pendingAcceptance?: boolean;
  // Admin view only (`GET /games/:id/details` for a verified admin of the
  // game's organization): what the payer was charged for THIS covered seat
  // (`GameJoiningIntent.amountCents`, minor units) and the PaymentIntent that
  // charged it. Absent for self-paid seats, legacy rows without a slot
  // amount, and every non-admin viewer.
  coveredAmountCents?: number;
  coveredPaymentIntentId?: string;
};

export type FormerCoveredSeatDto = {
  participant: UserDto;
  paidBy: UserDto;
  // What the payer was charged for this seat (`GameJoiningIntent.amountCents`,
  // minor units) and the succeeded PaymentIntent that charged it.
  coveredAmountCents: number;
  coveredPaymentIntentId: string;
};

export type NextoreGameParticipantDto = GameParticipantDto & {
  joiningPrice?: number;
  missingAmountToPay?: number;
};

export type Score = {
  teamId: number;
  value?: number;
};

export type Round = {
  scores: Score[];
};

export type Team = {
  id: number;
  players: string[];
};

export type Match = {
  rounds: Round[];
  teams: Team[];
  winnerTeamId: number | null | undefined; // Nullable initially
};

export enum GameType {
  PUBLIC = 'public',
  PRIVATE = 'private',
}

export enum GameStatus {
  UPCOMING = 'upcoming',
  SCORE_PROPOSED = 'score_proposed',
  SCORE_DISPUTED = 'score_disputed',
  ADMIN_REVIEW = 'admin_review',
  COMPLETED = 'completed',
  CANCELED = 'canceled',
}

export enum ScoreConfirmationStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  DISPUTED = 'disputed',
}

export enum GameRankedMode {
  RANKED = 'ranked',
  UNRANKED = 'unranked',
}

export type GameReplacementRequestDto = {
  token: string;
  type: ReplacementRequestType;
  status: ReplacementRequestStatus;
  originalUserName?: string;
  replacementUserName?: string;
  replacementGameId?: number;
  link?: string;
  createdAt: Date;
  expiresAt: Date;
  fulfilledAt?: Date;
};

export type GameRefundDto = {
  id: string;
  userName: string;
  /**
   * The payment intent this refund came off.
   *
   * Published so a client can NET a refund against the intent it reverses. A
   * PARTIAL refund deliberately leaves its intent in `succeeded` at its full
   * `amount` (`refund-payment-intent.ts`), so an intent list alone cannot say
   * what the venue still holds, and a refund list alone cannot say which
   * intents already dropped out (a FULL refund flips the intent to `refunded`,
   * and subtracting its row again would double-count the money back).
   * Attribution is the only thing that separates those two cases.
   */
  paymentIntentId: string;
  status: RefundStatus;
  reason: string;
  /**
   * The settled refund amount in MAJOR units (euros), per
   * `settledRefundAmountCents` — not `Refund.amountCents`, which is null on a
   * full-PI row. Summing the LIVE rows (SUCCESSFUL + RETRY; a FAILED refund
   * never left) of one payment intent can never exceed what that intent
   * charged, because a full row absorbs only the remainder.
   */
  amount: number;
  currency: string;
  createdAt: Date;
  processedAt?: Date;
};
